import type {
  ConcentrationTimePoint,
  TrapezoidalRule,
  TrapezoidalSegment,
  BlqRule,
} from '@bioavail/shared-types';
import { computeAucZeroToT } from './trapezoidal';
import { findCmaxIndex } from './cmax';

/**
 * Interpolate concentration at a target time.
 * Linear before Cmax, log after Cmax (PSD §4.2.10).
 */
export function interpolateConcentration(
  points: ConcentrationTimePoint[],
  targetTime: number,
): number {
  const sorted = [...points].sort((a, b) => a.time - b.time);
  if (targetTime <= sorted[0]!.time) return sorted[0]!.concentration;
  if (targetTime >= sorted[sorted.length - 1]!.time) {
    return sorted[sorted.length - 1]!.concentration;
  }

  const cmaxIndex = findCmaxIndex(sorted);

  for (let i = 0; i < sorted.length - 1; i++) {
    const p1 = sorted[i]!;
    const p2 = sorted[i + 1]!;
    if (targetTime >= p1.time && targetTime <= p2.time) {
      const frac = (targetTime - p1.time) / (p2.time - p1.time);
      const postCmax = i >= cmaxIndex;
      if (postCmax && p1.concentration > 0 && p2.concentration > 0 && p1.concentration !== p2.concentration) {
        // Log-linear interpolation.
        return Math.exp(
          Math.log(p1.concentration) + frac * (Math.log(p2.concentration) - Math.log(p1.concentration)),
        );
      }
      return p1.concentration + frac * (p2.concentration - p1.concentration);
    }
  }
  return 0;
}

/**
 * Partial AUC over a [start, end] window with boundary interpolation (PSD §4.2.10).
 * Blocks if the window falls outside the data range.
 */
export function computePartialAuc(
  inputPoints: ConcentrationTimePoint[],
  window: { start: number; end: number },
  rule: TrapezoidalRule,
  blqRule: BlqRule,
  lloq?: number,
): { aucPartial: number; segments: TrapezoidalSegment[] } {
  const sorted = [...inputPoints].sort((a, b) => a.time - b.time);
  const tMin = sorted[0]!.time;
  const tMax = sorted[sorted.length - 1]!.time;

  if (window.start < tMin || window.end > tMax) {
    throw new Error(
      `Partial AUC window [${window.start}, ${window.end}] is outside the data range [${tMin}, ${tMax}].`,
    );
  }
  if (window.start >= window.end) {
    throw new Error('Partial AUC window start must be before end.');
  }

  const bounded: ConcentrationTimePoint[] = [
    { time: window.start, concentration: interpolateConcentration(sorted, window.start), isBlq: false },
  ];
  for (const p of sorted) {
    if (p.time > window.start && p.time < window.end) {
      bounded.push(p);
    }
  }
  bounded.push({
    time: window.end,
    concentration: interpolateConcentration(sorted, window.end),
    isBlq: false,
  });

  const result = computeAucZeroToT(bounded, rule, blqRule, lloq);
  return { aucPartial: result.aucZeroToT, segments: result.segments };
}
